import { useEffect, useRef, ReactNode, RefObject } from "react";

export type SheetPosition = "left" | "right" | "top" | "bottom";

export type Props = {
  /** Whether sheet is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Sheet children */
  children?: ReactNode;
  /** Position of the sheet */
  position?: SheetPosition;
  /** Additional CSS classes for the root element */
  className?: string;
  /** Custom backdrop renderer */
  renderBackdrop?: (props: { onClose: () => void; children: ReactNode }) => ReactNode;
  /** Custom panel renderer */
  renderPanel?: (props: {
    children: ReactNode;
    ref: RefObject<HTMLDivElement>;
    position: SheetPosition;
  }) => ReactNode;
  /** Custom close button renderer */
  renderCloseButton?: (props: { onClose: () => void }) => ReactNode;
  /** Accessible label for the dialog */
  "aria-label"?: string;
  /** ID of element that labels the dialog */
  "aria-labelledby"?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base sheet/drawer component with no styling.
 * Handles escape key, scroll lock, and backdrop click.
 * Focus management (trapping, initial focus, restore) is the themed layer's responsibility.
 * Use render props to customize the appearance and animations.
 *
 * @example
 * // In shared/ui/Sheet.tsx
 * import { SheetBase, type Props as SheetBaseProps } from "@core/ui";
 *
 * export function Sheet({ className, ...props }: SheetBaseProps) {
 *   return (
 *     <SheetBase
 *       {...props}
 *       renderBackdrop={({ onClose, children }) => (
 *         <div className="fixed inset-0 bg-black/50" onClick={onClose}>
 *           {children}
 *         </div>
 *       )}
 *       renderPanel={({ children, ref, position }) => (
 *         <div ref={ref} className="bg-white h-full w-80">{children}</div>
 *       )}
 *     />
 *   );
 * }
 */
export function SheetBase({
  isOpen,
  onClose,
  children,
  position = "right",
  className,
  renderBackdrop,
  renderPanel,
  renderCloseButton,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "data-testid": testId = "sheet",
}: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow || "auto";
    };
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Default close button
  const defaultCloseButton = (
    <button
      type="button"
      onClick={onClose}
      data-sheet-close
      data-testid={`${testId}-close-button`}
      aria-label="Close"
    >
      ×
    </button>
  );

  // Sheet content
  const sheetContent = (
    <>
      {renderCloseButton ? renderCloseButton({ onClose }) : defaultCloseButton}
      {children}
    </>
  );

  // Default panel rendering
  const defaultPanel = (
    <div
      ref={sheetRef}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-testid={testId}
      data-position={position}
      onClick={(e) => e.stopPropagation()}
    >
      {sheetContent}
    </div>
  );

  const panel = renderPanel
    ? renderPanel({ children: sheetContent, ref: sheetRef, position })
    : defaultPanel;

  // Default backdrop rendering
  const defaultBackdrop = (
    <div
      data-sheet-backdrop
      onClick={handleBackdropClick}
      className={className}
      data-testid={`${testId}-backdrop`}
    >
      {panel}
    </div>
  );

  return renderBackdrop
    ? renderBackdrop({ onClose, children: panel })
    : defaultBackdrop;
}

SheetBase.displayName = "SheetBase";

